import { json, type LoaderFunctionArgs, type MetaFunction, type TypedResponse } from '@remix-run/cloudflare';
import { useEffect } from 'react';
import { ClientOnly } from 'remix-utils/client-only';
import { BaseChat } from '~/components/chat/BaseChat';
import { Chat } from '~/components/chat/Chat.client';
import { IndexLayout, type IndexLoaderData } from '~/layouts/IndexLayout';

import { handleWithAuth } from '~/lib/.server/auth-middleware';
import { setCookie } from '~/lib/.server/cookie';
import { getTokenStats } from '~/lib/.server/rateLimit';
import { withUserData } from '~/lib/.server/user';
import { INDEX_PAGE_TITLE, INDEX_PAGE_DESCRIPTION, REFERRAL_ID_PARAM } from '~/lib/constants';
import { applicationManager } from '~/lib/stores/applicationManager';
import { getMetaTags } from '~/utils/meta';

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return getMetaTags({
    user: data?.user,
    generalData: {
      title: INDEX_PAGE_TITLE,
      pageDescription: INDEX_PAGE_DESCRIPTION,
    },
  });
};

export async function loader(args: LoaderFunctionArgs) {
  return handleWithAuth(
    args,
    async ({ request }, session) => {
      const userData = await withUserData({ loggedIn: true }, session, request, true);
      const tokenStats = await getTokenStats(args, session, userData?.user);

      const data: IndexLoaderData = { ...userData, tokenStats };

      return json(data);
    },
    (): TypedResponse<IndexLoaderData> => {
      const rid = new URL(args.request.url).searchParams.get('rid');
      const headers = new Headers();

      if (rid) {
        const expiresInAYear = new Date();

        expiresInAYear.setFullYear(expiresInAYear.getFullYear() + 1);

        setCookie(headers, REFERRAL_ID_PARAM, rid, {
          expires: expiresInAYear,
        });
      }

      return json({ loggedIn: false } satisfies IndexLoaderData, { headers });
    },
  );
}

export default function Index() {
  useEffect(() => {
    applicationManager.loadApplicationsConfiguration({ background: true });
  }, []);

  return (
    <IndexLayout>
      <ClientOnly fallback={<BaseChat />}>{() => <Chat />}</ClientOnly>
    </IndexLayout>
  );
}
